import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { FeelingsPathway } from "@/components/feelings/FeelingsPathway";
import { FeelingsPathwaySection } from "@/components/dashboard/FeelingsPathwaySection";
import { CORE_FEELINGS, UNKNOWN_COLOR, ink, tint } from "@/data/feelings-pathway";

const TITLE = "How Are You Feeling? — Recovery Trauma™";
const DESCRIPTION =
  "A gentle, trauma-informed way to name a feeling, explore what may sit underneath it, notice what you might need, and find a practice that fits.";

export const Route = createFileRoute("/how-are-you-feeling/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/how-are-you-feeling" }],
  }),
  component: HowAreYouFeelingPage,
});

function HowAreYouFeelingPage() {
  const membership = useMembership();
  const hasAccess = membership.isMember || membership.isAdmin;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <h1 className="font-display text-2xl text-foreground sm:text-3xl">How are you feeling?</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Start where you are. Nothing here is a test, and there is no wrong answer.
      </p>
      <div className="mt-6">
        {membership.isAuthed ? (
          <FeelingsPathwaySection canCheckIn={hasAccess} />
        ) : (
          <FeelingsPathway variant="full" showIntro hasMemberAccess={false} />
        )}
      </div>

      <section className="mt-10" aria-labelledby="feeling-pages-heading">
        <h2 id="feeling-pages-heading" className="font-display text-xl text-foreground">
          Go straight to a feeling
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Each page walks through understanding the feeling, reflecting on it, naming what you might
          need, a short exercise, and saving it privately if you'd like to.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CORE_FEELINGS.map((f) => (
            <Link
              key={f.key}
              to="/how-are-you-feeling/$feeling"
              params={{ feeling: f.key }}
              style={{
                background: `linear-gradient(150deg, ${tint(f.color, 18)}, ${tint(f.color, 46)})`,
                borderColor: `color-mix(in oklab, ${f.color} 42%, white)`,
              }}
              className="on-tint flex min-h-[84px] flex-col justify-between rounded-2xl border-2 p-3.5 transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <span className="text-[15px] font-semibold" style={{ color: ink(f.color) }}>
                {f.label}
              </span>
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-foreground/75">
                {f.hint} <ArrowRight className="h-3 w-3" aria-hidden />
              </span>
            </Link>
          ))}
          <Link
            to="/how-are-you-feeling/$feeling"
            params={{ feeling: "unknown" }}
            style={{
              background: `linear-gradient(150deg, ${tint(UNKNOWN_COLOR, 18)}, ${tint(UNKNOWN_COLOR, 46)})`,
              borderColor: `color-mix(in oklab, ${UNKNOWN_COLOR} 42%, white)`,
            }}
            className="on-tint flex min-h-[84px] flex-col justify-between rounded-2xl border-2 p-3.5 transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="text-[15px] font-semibold" style={{ color: ink(UNKNOWN_COLOR) }}>
              I don't know what I'm feeling
            </span>
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-foreground/75">
              Start with the body <ArrowRight className="h-3 w-3" aria-hidden />
            </span>
          </Link>
        </div>
      </section>
    </main>
  );
}
